import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, RotateCcw, Image, Sparkles } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { useBrand, absolutize } from "../../lib/BrandContext";
import { Button } from "../ui/button";

/**
 * Inline card on /admin/settings — single global brand.
 * Uploads propagate app-wide via BrandContext.reload() on success.
 * Nothing here is multi-tenant (per Phase 4.8 scope).
 */
const LOGO_ACCEPT = ".png,.jpg,.jpeg,.webp,.svg,image/png,image/jpeg,image/webp,image/svg+xml";
const FAVICON_ACCEPT = ".png,.ico,.svg,image/png,image/x-icon,image/vnd.microsoft.icon,image/svg+xml";

export default function AdminBrandCard() {
  const { logoUrl, faviconUrl, intentPickerEnabled, reload } = useBrand();
  const [busy, setBusy] = useState(""); // "logo" | "favicon" | "flags" | ""
  const [pickerOn, setPickerOn] = useState(intentPickerEnabled);
  const logoInput = useRef(null);
  const faviconInput = useRef(null);

  // Keep the local switch in sync when brand context reloads.
  React.useEffect(() => { setPickerOn(intentPickerEnabled); }, [intentPickerEnabled]);

  const toggleIntentPicker = async (next) => {
    setPickerOn(next);
    setBusy("flags");
    try {
      await api.patch("/admin/settings/brand/flags", { intent_picker_enabled: next });
      toast.success(next ? "Homepage picker: shown" : "Homepage picker: hidden");
      await reload();
    } catch (e) {
      // Roll back the switch if the write failed.
      setPickerOn(!next);
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const upload = async (kind, file) => {
    if (!file) return;
    setBusy(kind);
    const form = new FormData();
    form.append("file", file);
    try {
      await api.post(`/admin/settings/brand/${kind}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${kind === "logo" ? "Logo" : "Favicon"} updated`);
      await reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally {
      setBusy("");
      if (kind === "logo" && logoInput.current) logoInput.current.value = "";
      if (kind === "favicon" && faviconInput.current) faviconInput.current.value = "";
    }
  };

  const reset = async (kind) => {
    if (!window.confirm(`Reset the ${kind}? The default VenturaZ ${kind === "logo" ? "wordmark" : "favicon"} will show everywhere.`)) return;
    setBusy(kind);
    try {
      await api.delete(`/admin/settings/brand/${kind}`);
      toast.success(`${kind === "logo" ? "Logo" : "Favicon"} reset`);
      await reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(""); }
  };

  return (
    <div
      data-testid="admin-brand-card"
      className="border border-ink/10 bg-white rounded-lg p-5 mb-6"
    >
      <div className="flex items-center gap-2 mb-1">
        <Sparkles size={14} className="text-navy" />
        <h3 className="text-sm font-heading text-navy">Brand · logo & favicon</h3>
      </div>
      <p className="text-[11px] text-ink-muted leading-relaxed mb-4">
        These are the single global brand assets — they replace the default wordmark and favicon
        across every page for every user (public + admin). Uploads take effect immediately.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <UploadTile
          testId="brand-logo-tile"
          label="Logo"
          hint="PNG · JPG · WEBP · SVG · max 2 MB · displays at 32-40px tall"
          previewUrl={logoUrl}
          previewClass="bg-navy p-3 flex items-center justify-center min-h-[80px]"
          onChoose={() => logoInput.current?.click()}
          onReset={() => reset("logo")}
          busy={busy === "logo"}
          hasAsset={!!logoUrl}
        />
        <UploadTile
          testId="brand-favicon-tile"
          label="Favicon"
          hint="PNG · ICO · SVG · max 256 KB · square, 32×32 or 64×64 recommended"
          previewUrl={faviconUrl}
          previewClass="bg-white border border-ink/10 p-3 flex items-center justify-center min-h-[80px]"
          previewImgClass="w-8 h-8 object-contain"
          onChoose={() => faviconInput.current?.click()}
          onReset={() => reset("favicon")}
          busy={busy === "favicon"}
          hasAsset={!!faviconUrl}
        />
      </div>

      {/* Homepage IntentPicker A/B toggle — Super Admin only. Failed writes
          silently roll the switch back so the UI never disagrees with the DB. */}
      <div className="mt-5 pt-5 border-t border-ink/10" data-testid="brand-flag-intent-picker">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-sm font-heading text-ink mb-1">Homepage intent picker</div>
            <p className="text-[11px] text-ink-muted leading-relaxed max-w-lg">
              Show the 3-card segmentation strip (Raise Equity · Raise Debt · Invest) below the
              homepage hero. Turn it off to A/B test signup conversion against the hero-only baseline.
            </p>
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer select-none">
            <span className={`text-[10px] font-mono uppercase tracking-widest ${pickerOn ? "text-navy" : "text-ink-muted"}`}>
              {pickerOn ? "Visible" : "Hidden"}
            </span>
            <span className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${pickerOn ? "bg-navy" : "bg-ink/25"} ${busy === "flags" ? "opacity-70" : ""}`}>
              <input
                type="checkbox"
                checked={pickerOn}
                disabled={busy === "flags"}
                onChange={(e) => toggleIntentPicker(e.target.checked)}
                className="sr-only"
                data-testid="brand-flag-intent-picker-toggle"
              />
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${pickerOn ? "translate-x-4" : "translate-x-0.5"}`} />
            </span>
          </label>
        </div>
      </div>
      <input
        ref={logoInput}
        type="file"
        accept={LOGO_ACCEPT}
        onChange={(e) => upload("logo", e.target.files?.[0])}
        className="hidden"
        data-testid="brand-logo-input"
      />
      <input
        ref={faviconInput}
        type="file"
        accept={FAVICON_ACCEPT}
        onChange={(e) => upload("favicon", e.target.files?.[0])}
        className="hidden"
        data-testid="brand-favicon-input"
      />
    </div>
  );
}

function UploadTile({ testId, label, hint, previewUrl, previewClass, previewImgClass, onChoose, onReset, busy, hasAsset }) {
  return (
    <div className="rounded-md border border-ink/10 overflow-hidden" data-testid={testId}>
      <div className={`${previewClass || ""}`}>
        {previewUrl ? (
          // Absolutize handles both /api/... paths and full URLs.
          <img
            src={absolutize(previewUrl) || previewUrl}
            alt={label}
            className={previewImgClass || "max-h-16 max-w-[80%] object-contain"}
            data-testid={`${testId}-preview`}
          />
        ) : (
          <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 flex items-center gap-1">
            <Image size={12} /> No {label.toLowerCase()} uploaded
          </div>
        )}
      </div>
      <div className="p-3 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs font-heading text-navy">{label}</div>
          <div className="text-[10px] text-ink-muted truncate">{hint}</div>
        </div>
        <div className="flex gap-1 shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={onChoose}
            disabled={busy}
            data-testid={`${testId}-upload-btn`}
            className="text-[11px] h-7 gap-1"
          >
            <Upload size={11} /> {busy ? "Uploading…" : hasAsset ? "Replace" : "Upload"}
          </Button>
          {hasAsset && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onReset}
              disabled={busy}
              data-testid={`${testId}-reset-btn`}
              className="text-[11px] h-7 gap-1 text-red-600 hover:bg-red-50"
            >
              <RotateCcw size={11} /> Reset
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
